"use client";

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, Users, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuditLogAnalytics } from '@/hooks/audit';
import type { AuditLogFilters } from '@/hooks/audit';

interface AuditLogAnalyticsProps {
  filters: AuditLogFilters;
}

export const AuditLogAnalytics: React.FC<AuditLogAnalyticsProps> = ({
  filters,
}) => {
  const { t } = useTranslation();
  const { data: analyticsData, isLoading, error } = useAuditLogAnalytics({
    startDate: filters.startDate,
    endDate: filters.endDate,
    userId: filters.userId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted animate-pulse rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded mb-2"></div>
                <div className="h-3 bg-muted animate-pulse rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !analyticsData) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-muted-foreground">{t('auditLogs.errorLoadingAnalytics')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    trend, 
    color = 'default' 
  }: {
    title: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    trend?: { value: number; isPositive: boolean };
    color?: 'default' | 'success' | 'destructive' | 'warning';
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend && (
          <p className={`text-xs ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {trend.isPositive ? '+' : ''}{trend.value}% from last period
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('auditLogs.totalLogs')}
          value={analyticsData.totalLogs.toLocaleString()}
          icon={Activity}
        />
        <StatCard
          title={t('auditLogs.successRate')}
          value={`${analyticsData.successRate}%`}
          icon={CheckCircle}
          color="success"
        />
        <StatCard
          title={t('auditLogs.activeUsers')}
          value={analyticsData.topUsers.length}
          icon={Users}
        />
        <StatCard
          title={t('auditLogs.failureRate')}
          value={`${(100 - analyticsData.successRate).toFixed(1)}%`}
          icon={XCircle}
          color="destructive"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t('auditLogs.topActions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData.topActions.map((item: any, index: number) => (
                <div key={item.action} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                    <span className="text-sm font-medium">
                      {item.action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{item.count}</div>
                    <div className="text-xs text-muted-foreground">{item.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('auditLogs.topUsers')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData.topUsers.map((item: any, index: number) => {
                const name = item.user?.firstName && item.user?.lastName
                  ? `${item.user.firstName} ${item.user.lastName}`
                  : item.user?.email || t('common.unknown');
                const email = item.user?.email || '';
                const initials = name
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <div key={item.userId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className="text-xs shrink-0">
                        #{index + 1}
                      </Badge>
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={(item.user?.avatarUrl || item.user?.photo) ?? undefined} alt={name} />
                        <AvatarFallback>
                          <span className="text-[10px] font-medium">{initials}</span>
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate max-w-[220px]">
                          {name}
                        </div>
                        {email && (
                          <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                            {email}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">{item.count}</div>
                      <div className="text-xs text-muted-foreground">{item.percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      
    </div>
  );
};
